import { Link, useLocation } from "wouter";
import { useClerk } from "@clerk/react";
import { useEffect, useState } from "react";
import { 
  LayoutDashboard, 
  BookOpen, 
  Stethoscope, 
  FlaskConical, 
  GraduationCap, 
  MessageSquare,
  LogOut,
  Activity,
  BookOpenCheck,
  Award,
  ShieldCheck,
  Settings,
  CalendarDays,
  BookUser,
  TrendingUp,
  Inbox,
  AlertTriangle,
  Users,
  PillBottle,
  Mic,
} from "lucide-react";
import { LastSignInBanner } from "@/components/ui/last-signin-banner";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { SessionNoticeBanner } from "@/components/ui/session-notice-banner";
import { HcpVerificationBanner } from "@/components/ui/hcp-verification-banner";
import { LegalFooter } from "@/components/layout/legal-footer";
import { GlobalSearch } from "@/components/ui/global-search";
import { DashboardHeader } from "@/components/layout/dashboard-header";

import { clearRheumHubLocalStorage } from "@/lib/local-storage-keys";

type PracticeLevel = "specialist" | "advanced" | "consultant";

const LEVEL_KEY = "rheumhub_practice_level";

const LEVEL_SHORT: Record<PracticeLevel, string> = {
  specialist: "Specialist",
  advanced: "Advanced",
  consultant: "Consultant",
};

interface PortfolioWidgetState {
  achieved: number;
  total: number;
  level: PracticeLevel;
  status: "loading" | "ready" | "error";
}

function usePortfolioWidget(): PortfolioWidgetState {
  const getLevel = (): PracticeLevel =>
    (localStorage.getItem(LEVEL_KEY) as PracticeLevel) ?? "specialist";

  const [state, setState] = useState<PortfolioWidgetState>({
    achieved: 0,
    total: 0,
    level: getLevel(),
    status: "loading",
  });

  const fetchForLevel = (level: PracticeLevel) => {
    setState((prev) => ({ ...prev, level, status: "loading" }));
    fetch(`/api/competency/portfolio?practiceLevel=${level}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data: { domains: { achieved: number; total: number }[] } | { achieved: number; total: number }[]) => {
        const domains: { achieved: number; total: number }[] = Array.isArray(data)
          ? data
          : (data?.domains ?? []);
        const achieved = domains.reduce<number>((s, d) => s + d.achieved, 0);
        const total = domains.reduce<number>((s, d) => s + d.total, 0);
        setState({ achieved, total, level, status: "ready" });
      })
      .catch(() => {
        setState((prev) => ({ ...prev, level, status: "error" }));
      });
  };

  useEffect(() => {
    fetchForLevel(getLevel());

    const onStorage = (e: StorageEvent) => {
      if (e.key === LEVEL_KEY) {
        fetchForLevel((e.newValue as PracticeLevel) ?? "specialist");
      }
    };
    const onLevelChange = (e: Event) => {
      const level = (e as CustomEvent<PracticeLevel>).detail;
      if (level) fetchForLevel(level);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("rheumhub-level-change", onLevelChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("rheumhub-level-change", onLevelChange);
    };
  }, []);

  return state;
}

function ringColor(pct: number): string {
  if (pct >= 0.75) return "#2dd4bf";
  if (pct >= 0.25) return "#f59e0b";
  return "#64748b";
}

function PortfolioRing({ achieved, total, status }: { achieved: number; total: number; status: string }) {
  const r = 22;
  const circ = 2 * Math.PI * r;
  const pct = total > 0 ? achieved / total : 0;
  const offset = circ - pct * circ;
  const color = status === "ready" ? ringColor(pct) : "#1e293b";

  return (
    <svg width="52" height="52" viewBox="0 0 52 52" className="shrink-0">
      <circle cx="26" cy="26" r={r} fill="none" stroke="#1e293b" strokeWidth="5" />
      {status === "ready" && total > 0 && (
        <circle
          cx="26" cy="26" r={r}
          fill="none"
          stroke={color}
          strokeWidth="5"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 26 26)"
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      )}
      <text
        x="26" y="30"
        textAnchor="middle"
        fontSize="11"
        fontWeight="600"
        fill={status === "ready" ? color : "#475569"}
      >
        {status === "ready" ? `${Math.round(pct * 100)}%` : status === "loading" ? "…" : "–"}
      </text>
    </svg>
  );
}

export function NurseLayout({ children, title }: { children: React.ReactNode, title: string }) {
  const [location] = useLocation();
  const { signOut } = useClerk();
  const portfolio = usePortfolioWidget();
  const isAdmin = useIsAdmin();

  useEffect(() => {
    document.title = `${title} | RheumHub`;
    return () => { document.title = "RheumHub"; };
  }, [title]);

  const navItems = [
    { href: "/nurse", label: "Dashboard", icon: LayoutDashboard },
    { href: "/nurse/guidelines", label: "Guidelines", icon: BookOpen },
    { href: "/nurse/conditions", label: "Conditions", icon: Stethoscope },
    { href: "/nurse/pharmacology", label: "Pharmacology", icon: FlaskConical },
    { href: "/nurse/learn", label: "Learning Hub", icon: BookOpenCheck },
    { href: "/nurse/events", label: "Events", icon: CalendarDays },
    { href: "/nurse/directory", label: "Directory", icon: BookUser },
    { href: "/nurse/requests", label: "Incoming Requests", icon: Inbox },
    { href: "/nurse/scenarios", label: "Scenarios", icon: GraduationCap },
    { href: "/nurse/chat", label: "AI Assistant", icon: MessageSquare },
    { href: "/nurse/dictation", label: "Note Dictation", icon: Mic },
    { href: "/nurse/competency", label: "Competency Portfolio", icon: Award },
    { href: "/nurse/career", label: "Career Pathway", icon: TrendingUp },
  ];

  const governanceItems = [
    { href: "/nurse/ai-governance", label: "AI Governance", icon: ShieldCheck },
    { href: "/hcp/safety-report", label: "Report Safety Concern", icon: AlertTriangle },
    { href: "/nurse/settings", label: "Your Data", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <SessionNoticeBanner />
      <LastSignInBanner />
      <HcpVerificationBanner />
      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-64 bg-[#002470] text-white/75 flex flex-col fixed inset-y-0 z-20">
          <div className="p-6 flex items-center gap-3 text-white">
            <Activity className="h-6 w-6 text-[#7bb8ff]" />
            <span className="text-xl font-bold tracking-tight">RheumHub</span>
          </div>
          
          <div className="px-6 py-2 text-xs font-semibold uppercase tracking-wider text-[#7bb8ff]/60">
            Nurse Portal
          </div>

          <nav className="sidebar-nav flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = location === item.href || (location.startsWith(item.href) && item.href !== "/nurse");
              return (
                <Link 
                  key={item.href} 
                  href={item.href}
                  className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                    isActive 
                      ? "bg-[#7bb8ff]/15 text-[#7bb8ff]" 
                      : "hover:bg-white/10 hover:text-white"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <item.icon className={`h-4 w-4 shrink-0 ${isActive ? "text-[#7bb8ff]" : "text-white/35"}`} />
                  {item.label}
                </Link>
              );
            })}

            <div className="pt-3 pb-1">
              <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-white/20">
                Governance
              </div>
            </div>

            {governanceItems.map((item) => {
              const isActive = location === item.href || location.startsWith(item.href);
              return (
                <Link 
                  key={item.href} 
                  href={item.href}
                  className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                    isActive 
                      ? "bg-[#7bb8ff]/15 text-[#7bb8ff]" 
                      : "hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <item.icon className={`h-4 w-4 shrink-0 ${isActive ? "text-[#7bb8ff]" : "text-white/35"}`} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="mx-4 mb-3">
            <Link href="/nurse/competency">
              <div className="bg-black/25 rounded-xl p-3 border border-white/10 hover:border-[#7bb8ff]/30 transition-colors cursor-pointer group">
                <div className="flex items-center gap-3">
                  <PortfolioRing
                    achieved={portfolio.achieved}
                    total={portfolio.total}
                    status={portfolio.status}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white/80 group-hover:text-[#7bb8ff] transition-colors leading-tight">
                      Portfolio
                    </p>
                    <p className="text-xs text-white/40 mt-0.5 truncate">
                      {LEVEL_SHORT[portfolio.level]}
                    </p>
                    {portfolio.status === "ready" && (
                      <p className="text-xs text-white/50 mt-0.5">
                        {portfolio.achieved}/{portfolio.total} achieved
                      </p>
                    )}
                    {portfolio.status === "error" && (
                      <p className="text-xs text-white/25 mt-0.5">Unavailable</p>
                    )}
                    {portfolio.status === "loading" && (
                      <p className="text-xs text-white/25 mt-0.5">Loading…</p>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          </div>

          <div className="p-4 border-t border-white/10 space-y-1">
            {isAdmin && (
              <Link
                href="/admin"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  location.startsWith("/admin")
                    ? "bg-[#7bb8ff]/15 text-[#7bb8ff]"
                    : "hover:bg-white/10 hover:text-white text-white/40"
                }`}
              >
                <Users className="h-5 w-5" />
                Admin Panel
              </Link>
            )}
            <button 
              onClick={() => { clearRheumHubLocalStorage(); signOut({ redirectUrl: "/" }); }}
              className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-white/10 transition-colors text-white/40 hover:text-white"
              data-testid="nav-signout"
            >
              <LogOut className="h-5 w-5" />
              Sign out
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 pl-64 flex flex-col min-h-screen">
          <DashboardHeader title={title} profileLink="/nurse/profile" />
          <div className="flex-1 p-8">
            <div className="max-w-6xl mx-auto">
              {children}
            </div>
          </div>
          <LegalFooter
            copyright="© RheumHub · For UK healthcare professionals only · AI responses do not constitute clinical advice"
            extraLinks={[{ label: "Clinical Safety", href: "/clinical-safety" }]}
          />
        </main>
      </div>
    </div>
  );
}
